import { create } from 'zustand';
import { getChatbotConfigSection, updateChatbotConfigSection } from '../utils/chatbotConfigAccess';
import { DEFAULT_MINI_APPS, type MiniAppId } from '../utils/miniAppsIcons';
import { useMultilingualSettings } from './useMultilingualSettings';

interface MiniApp {
  id: MiniAppId;
  title: string;
  label: string;
  enabled: boolean;
  buttonVisible: boolean;
  icon?: any;
}

interface ButtonColors {
  startColor: string;
  endColor: string;
  textColor: string;
}

const DEFAULT_BUTTON_COLORS: ButtonColors = {
  startColor: '#7C3AED',
  endColor: '#2DD4BF',
  textColor: '#FFFFFF'
};

interface MiniAppsStore {
  miniApps: MiniApp[];
  buttonColors: ButtonColors;
  loading: boolean;
  error: Error | null;
  initialized: boolean;
  
  toggleMiniApp: (id: MiniAppId, chatbotPublicId: string) => Promise<void>;
  updateMiniAppLabel: (id: MiniAppId, label: string, chatbotPublicId: string) => Promise<void>;
  reorderMiniApps: (newOrder: MiniApp[], chatbotPublicId: string) => Promise<void>;
  setButtonColors: (colors: Partial<ButtonColors>, chatbotPublicId: string) => Promise<void>;
  initializeMiniApps: (chatbotPublicId: string) => Promise<void>;
  toggleButtonVisibility: (id: MiniAppId, chatbotPublicId: string) => Promise<void>;
}

export const useMiniApps = create<MiniAppsStore>((set, get) => ({
  miniApps: DEFAULT_MINI_APPS.map(app => ({ ...app, buttonVisible: true })),
  buttonColors: DEFAULT_BUTTON_COLORS,
  loading: false,
  error: null,
  initialized: false,

  toggleMiniApp: async (id, chatbotPublicId) => {
    if (!chatbotPublicId) return;
    
    try {
      set({ loading: true, error: null });
      
      const newMiniApps = get().miniApps.map(app =>
        app.id === id ? { ...app, enabled: !app.enabled } : app
      );

      const success = await updateChatbotConfigSection(
        'mini_apps', 
        newMiniApps, 
        chatbotPublicId
      );

      if (!success) {
        throw new Error('Failed to toggle mini app state');
      }

      set({ miniApps: newMiniApps, loading: false });
    } catch (err) {
      set({ 
        error: err instanceof Error ? err : new Error('Unknown error toggling mini app'),
        loading: false 
      });
    }
  },

  toggleButtonVisibility: async (id, chatbotPublicId) => {
    if (!chatbotPublicId) return;
    
    try {
      set({ loading: true, error: null });
      
      const newMiniApps = get().miniApps.map(app =>
        app.id === id ? { ...app, buttonVisible: !app.buttonVisible } : app
      );

      const success = await updateChatbotConfigSection(
        'mini_apps', 
        newMiniApps, 
        chatbotPublicId
      );

      if (!success) {
        throw new Error('Failed to toggle button visibility');
      }

      set({ miniApps: newMiniApps, loading: false });
    } catch (err) {
      set({ 
        error: err instanceof Error ? err : new Error('Unknown error toggling button visibility'),
        loading: false 
      });
    }
  },

  updateMiniAppLabel: async (id, label, chatbotPublicId) => {
    if (!chatbotPublicId) return;
    
    try {
      set({ loading: true, error: null });
      
      // Get multilingual settings
      const { settings: multilingualSettings } = useMultilingualSettings.getState();
      
      // If multilingual is enabled, update the translation for the current language
      if (multilingualSettings.enabled && multilingualSettings.currentLanguage) {
        const currentLang = multilingualSettings.currentLanguage;
        const translations = multilingualSettings.translations || {};
        const langTranslations = translations[currentLang] || {};
        
        // Update the mini app translation
        const updatedTranslations = {
          ...translations,
          [currentLang]: {
            ...langTranslations,
            miniApps: (langTranslations.miniApps || []).map((app: any) =>
              app.id === id ? { ...app, label } : app
            )
          }
        };
        
        // Update multilingual settings
        await updateChatbotConfigSection(
          'multilingual_settings',
          {
            ...multilingualSettings,
            translations: updatedTranslations
          },
          chatbotPublicId
        );
      }

      // Always update the base mini apps configuration
      const newMiniApps = get().miniApps.map(app =>
        app.id === id ? { ...app, label } : app
      );

      const success = await updateChatbotConfigSection(
        'mini_apps', 
        newMiniApps, 
        chatbotPublicId
      );

      if (!success) {
        throw new Error('Failed to update mini app label');
      }

      set({ miniApps: newMiniApps, loading: false });
    } catch (err) {
      set({ 
        error: err instanceof Error ? err : new Error('Unknown error updating mini app label'),
        loading: false 
      });
    }
  },

  reorderMiniApps: async (newOrder, chatbotPublicId) => {
    if (!chatbotPublicId) return;
    
    try {
      set({ loading: true, error: null });
      
      const success = await updateChatbotConfigSection(
        'mini_apps', 
        newOrder, 
        chatbotPublicId
      );

      if (!success) {
        throw new Error('Failed to reorder mini apps');
      }

      set({ miniApps: newOrder, loading: false });
    } catch (err) {
      set({ 
        error: err instanceof Error ? err : new Error('Unknown error reordering mini apps'),
        loading: false 
      });
    }
  },

  setButtonColors: async (colors, chatbotPublicId) => {
    if (!chatbotPublicId) return;
    
    try {
      set({ loading: true, error: null });
      
      const currentColors = get().buttonColors;
      const updatedColors = { ...currentColors, ...colors };

      const success = await updateChatbotConfigSection(
        'button_colors', 
        updatedColors, 
        chatbotPublicId
      );

      if (!success) {
        throw new Error('Failed to update button colors');
      }

      set({ buttonColors: updatedColors, loading: false });
    } catch (err) {
      set({ 
        error: err instanceof Error ? err : new Error('Unknown error updating button colors'),
        loading: false 
      });
    }
  },

  initializeMiniApps: async (chatbotPublicId) => {
    try {
      set({ loading: true, error: null });
      
      // Get mini apps and button colors from the database
      const miniApps = await getChatbotConfigSection<MiniApp[]>(
        'mini_apps',
        chatbotPublicId
      );
      
      const buttonColors = await getChatbotConfigSection<ButtonColors>(
        'button_colors',
        chatbotPublicId
      );

      // Get multilingual settings
      const { settings: multilingualSettings } = useMultilingualSettings.getState();
      
      // If multilingual is enabled and we have translations for the current language,
      // apply them to the mini apps
      let translatedMiniApps = DEFAULT_MINI_APPS;
      if (multilingualSettings.enabled && multilingualSettings.currentLanguage) {
        const currentLang = multilingualSettings.currentLanguage;
        const translations = multilingualSettings.translations[currentLang];
        
        if (translations?.miniApps) {
          translatedMiniApps = DEFAULT_MINI_APPS.map(defaultApp => {
            const translation = translations.miniApps.find((t: any) => t.id === defaultApp.id);
            const existingApp = miniApps?.find(app => app.id === defaultApp.id);
            
            return {
              ...defaultApp,
              ...existingApp,
              enabled: existingApp?.enabled ?? defaultApp.enabled,
              label: translation?.label || defaultApp.label,
              title: translation?.title || defaultApp.title,
              icon: defaultApp.icon // Always keep the icon from DEFAULT_MINI_APPS
            };
          });
        }
      } else if (miniApps) {
        // If multilingual is not enabled, use the base mini apps configuration
        translatedMiniApps = DEFAULT_MINI_APPS.map(defaultApp => {
          const existingApp = miniApps.find(app => app.id === defaultApp.id);
          return {
            ...defaultApp,
            ...existingApp,
            enabled: existingApp?.enabled ?? defaultApp.enabled,
            icon: defaultApp.icon // Always keep the icon from DEFAULT_MINI_APPS
          };
        });
      }
      
      set({ 
        miniApps: translatedMiniApps,
        buttonColors: buttonColors?.startColor ? buttonColors : DEFAULT_BUTTON_COLORS,
        initialized: true,
        loading: false,
        error: null
      });
    } catch (err) {
      set({ 
        error: err instanceof Error ? err : new Error('Unknown error initializing mini apps'),
        miniApps: DEFAULT_MINI_APPS,
        buttonColors: DEFAULT_BUTTON_COLORS,
        initialized: true,
        loading: false 
      });
    }
  }
}));

// Subscribe to multilingual settings changes
useMultilingualSettings.subscribe((state, prevState) => {
  // Vérifier les changements qui affectent les mini-apps
  const enabledChanged = state.settings.enabled !== prevState.settings.enabled;
  const currentLanguage = state.settings.currentLanguage;
  const prevLanguage = prevState.settings.currentLanguage;
  const languageChanged = currentLanguage !== prevLanguage;
  
  if (enabledChanged || languageChanged) {
    if (state.settings.enabled && currentLanguage) {
      // Récupérer les traductions pour la langue actuelle
      const translations = currentLanguage ? state.settings.translations[currentLanguage]?.miniApps : undefined;
      
      if (translations && translations.length > 0) {
        // Mettre à jour les miniapps avec les traductions disponibles
        useMiniApps.setState(store => {
          // Copie profonde pour éviter les problèmes de référence
          const updatedMiniApps = [...store.miniApps];
          
          // Appliquer les traductions à chaque mini app
          for (let i = 0; i < updatedMiniApps.length; i++) {
            const app = updatedMiniApps[i];
            const translation = translations.find(t => t.id === app.id);
            
            if (translation) {
              updatedMiniApps[i] = {
                ...app,
                label: translation.label || app.label,
                title: translation.title || app.title
              };
            }
          }
          
          return { miniApps: updatedMiniApps };
        });
      }
    }
  }
});